import { Box, Button, Flex, Heading, Text, VStack, Spinner } from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import SummaryTickets from "./SummaryTickets";
import { useNavigate } from "react-router-dom";
import OrdersService from "../../../api/services/OrdersService";
import {
    Cookie
} from '../../../utils/cookie-utils';
import { useAppToast } from "../../../hooks/useAppToast";

const ConfirmationStep = ({ data, t }) => {
    const navigate = useNavigate();
    const [error, setError] = useState(null);
    const [order, setOrder] = useState(null);
    const [orderId, setOrderId] = useState(null);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const redirectedRef = useRef(false);
    const toast = useAppToast();

    const eventId = data?.id ?? data?.eventId;
    const isFree = !!data?.isFree;

    const isCompany = !!data?.isCompany;
    const buyer = useMemo(() => ({
        name: data?.firstName || "",
        surName: data?.lastName || "",
        email: data?.email || "",
        phone: data?.phone || "",
        info: data?.info || "",
        isCompany: isCompany,
        buyerCompanyName: data?.buyerCompanyName || "",
        buyerNIP: data?.buyerNIP || "",
        buyerAddress: data?.buyerAddress || "",
        buyerPostalCode: data?.buyerPostalCode || "",
        buyerCity: data?.buyerCity || "",
        newsletterConsent: data?.newsletterConsent ?? false,
        organizerNewsletterConsent: data?.organizerNewsletterConsent ?? false,
        commercialInfoConsent: data?.commercialInfoConsent ?? false,
        profilingConsent: data?.profilingConsent ?? false,
    }), [data, isCompany]);

    const items = useMemo(() => {
        if (isFree) return [];
        const q = data?.cart?.quantities || {};
        return Object.entries(q)
            .map(([eventTicketTypeId, quantity]) => ({
                eventTicketTypeId,
                quantity: Number(quantity) || 0,
            }))
            .filter(x => x.quantity > 0);
    }, [data, isFree]);

    const freeQuantity = useMemo(() => {
        if (!isFree) return undefined;
        const qty = Number(data?.cart?.quantity || 0);
        return qty > 0 ? qty : undefined;
    }, [data, isFree]);

    const participants = useMemo(() => {
        const a = data?.attendees || [];
        return a.map(p => ({
            ...(p.eventTicketTypeId ? { eventTicketTypeId: p.eventTicketTypeId } : {}),
            name: p.firstName ?? p.name ?? "",
            surName: p.lastName ?? p.surName ?? "",
            email: p.email ?? "",
            phone: p.phone ?? "",
        }));
    }, [data]);

    useEffect(() => {
        (async () => {
            try {
                setError(null);
                if (!eventId) {
                    throw new Error(t('order.confirmation.empty.eventId'));
                }
                if (!buyer?.email) {
                    throw new Error(t('order.confirmation.empty.buyer'));
                }
                const isBuyerValid = isCompany 
                    ? !!buyer?.buyerCompanyName 
                    : (!!buyer?.name && !!buyer?.surName);

                if (!isBuyerValid) {
                    throw new Error(t('order.confirmation.empty.buyer'));
                }
                if (!items && !freeQuantity) {
                    throw new Error(t('order.confirmation.empty.positions'));
                }

                const idempotencyKey =
                    (crypto)?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;

                const payload = {
                    eventId,
                    buyer,
                    items,                // undefined dla free
                    freeQuantity,         // undefined dla paid
                    participants,
                    discountCode: data?.discountCode || undefined,
                };

                const order = await OrdersService.create(payload, idempotencyKey);
                const { orderId, number, status, totalGross, redirectUrl, tickets } = order;

                setOrder(order);
                setOrderId(orderId);
                Cookie.removeAll(eventId);
                if (redirectUrl) {
                    setIsRedirecting(true);
                    toast({
                        title: t("order.confirmation.payment.redirect") || "Przekierowanie do płatności…",
                        status: "info",
                        duration: 2500,
                        isClosable: true,
                    });
                    if (!redirectedRef.current) {
                        redirectedRef.current = true;
                        window.location.assign(redirectUrl);
                    }
                } else {
                    // free / od razu sfinalizowane
                    toast({
                        title: t("order.confirmation.payment.success") || "Zamówienie zapisane",
                        status: "success",
                        duration: 2500,
                        isClosable: true,
                    });
                }
            } catch (e) {
                setError(e);
                console.error(e);
                const errCode = e?.response?.data?.code;
                const errDescription = errCode
                    ? t(errCode)
                    : (e?.response?.data?.message || e?.message || t('order.confirmation.payment.fail'));
                toast({
                    title: t("order.confirmation.payment.fail") || "Nie udało się sfinalizować zamówienia",
                    description: errDescription,
                    status: "error",
                    duration: 7000,
                    isClosable: true,
                });
                // wróć do wyboru biletów
                // navigate("/");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [order]);

    const dwn = (ticketId, token) => {
        OrdersService.downloadTicket(ticketId, token);
    };
    const ticketTypes = data?.ticketTypes ?? [];
    const attendees = data?.attendees ?? [];

    if (isRedirecting) {
        return (
            <VStack spacing={6} textAlign="center" w={"full"} gap="48px" py="100px">
                <Spinner size="xl" color="#7A1F35" thickness="4px" />
                <Text fontFamily="body" fontSize="24px" fontWeight="700" color="#1E1517">
                    {t("order.confirmation.payment.redirect", "Przekierowanie do płatności…")}
                </Text>
            </VStack>
        );
    }

    return (
        <VStack spacing={6} textAlign="center" w={"full"} gap="48px">
            <Flex flexDirection={"column"} size="lg" alignItems={"center"} gap="32px">
                <svg width="96" height="96" viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="48" cy="48" r="48" fill={error ? "orange" : "#7A1F35"} />
                    <path d="M28 50L42 64L68 36" stroke="white" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <Heading as="h1" fontSize="28px" fontWeight="800" color="#1E1517" fontFamily="body">
                    {error
                        ? (error?.response?.data?.code
                            ? t(error.response.data.code)
                            : (error?.response?.data?.message || error?.message || t('order.confirmation.payment.fail')))
                        : (isFree ? t('order.confirmation.free.title') : t('order.confirmation.success'))}
                </Heading>
                {!error && isFree && (
                    <Text fontSize="lg" color="#6E6064" fontFamily="body" maxW="400px">
                        {t('order.confirmation.free.message')}
                    </Text>
                )}
            </Flex>
            {orderId ? (
                <Flex flexDirection={"column"}>
                    <Box fontFamily="body" fontSize="20px" fontWeight="500" gap="24px" alignItems={"center"}
                        textAlign={"center"}>
                        {!isFree && (
                            <Box mb="24px" color={'#4B3F42'} fontFamily="body">
                                <Text fontWeight="700">
                                    {t('order.confirmation.payment.title')}
                                </Text>
                                <Text>
                                    {t('order.confirmation.payment.message')}
                                </Text>
                            </Box>
                        )}
                        <Text color={'#A8A8A8'}>{t('order.form.number')} {order?.number}</Text>
                    </Box>
                </Flex>
            ) : (
                !error &&
                <Text>{t('order.processing')}</Text>
            )}
            {order &&
                <Box textAlign={"left"} w={"full"} mt="48px">
                    <Heading size="md" mb="16px">
                        <Flex justifyContent={"space-between"}>
                            <Text>{t('order.form.your_tickets')}</Text>
                        </Flex>
                    </Heading>
                    <SummaryTickets attendees={attendees} ticketTypes={ticketTypes} dwn={dwn} order={order} t={t} />
                </Box>
            }

            <Flex justify="space-between" gap="16px">
                <Button
                    bgColor={'#FFFFFF'}
                    color={'#7A1F35'}
                    fontFamily="body"
                    fontWeight="400"
                    fontSize="18px"
                    alignSelf="flex-end"
                    py="30px"
                    px="50px"
                    gap="12px"
                    style={{ border: '1.5px solid #7A1F35' }}
                    onClick={() => navigate("/")}>
                    <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10.5 20L3 12.5M3 12.5L10.5 5M3 12.5L21 12.5" stroke="#7A1F35" stroke-width="1.5"
                            stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    {t('order.form.to_main')}
                </Button>
            </Flex>
        </VStack>
    );
}

export default ConfirmationStep;
